import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SecureStateType } from '../../constants/enums';
import { containsBadWord } from '../../utilities/ageGate';
import ButtonLeft from '../Buttons/ButtonLeft';
import ButtonRight from '../Buttons/ButtonRight';
import { useSecureState } from '../Hooks/useSecureState';
import { useThrottledCallback } from '../Hooks/useThrottledCallback';
import Tag from './Tag';
import "./TagsPanel.linaria.module.css!=!./TagsPanel?./TagsPanel.linaria.module.css?css=H4sIAAAAAAAAA7VV226jMBB971dkH6q2Ul1hCE1CPsYyYMDB2MQ2BoL67ytSzCUlq92VwkOQZ87czhziD6wLVOQoc4vi9NmVQlFNBQ8kYVhTQ45fTx89hLgItqwkPu8yQtNMB1unbKwXS2SkiUizPHUJIw2IqSTRd1JR24iQIHg+RFF6uTl+x9QSlwEX/ctGZAhmn+JUXZanDjOackA1KVQQEa6JtBEHhaBuampYF1NVMtwGfXLrNi2CQp2Fa7qaxjoLoOM8W2dKEY3LJN12BZYp5YEzZ6KuUsV9ywSutOi9Otbu+eCPxUBNwpxqEIrmeGu7NjIaC3U1zIFXwBx9w6ONuTGvIac2wA9yFxUWDpt/brwbvrKEedmre3TYzAvrSobZVlQkBWMgJBk2VMhAFULo7FjiOKY8BVqUASybozBEJkzUoLku5RctSiE15touT+colZnYya4PmSm4yJHSvN7vp08Ah0qwSo+fQBItFDgdu1OlNE1aEAmuCdc3MuwFM+hsEMxcaPUZ5eGOm0t3AZTHpAngWPBsNchIoicFwizbaqMQi8PP2gQBTjSRg4Y9f5oJOv6WKWarW+BKE9AhTaEwSllbJ4UFloL2cwBiCNcq4IKPXMCDtz+dGwTbNomjnY2wBLy8jEDPlzHxUeu6poYWF+IoT6WoeBwwygmWIJU4poTr16fN7NFiI/tm3xdWg+UrAFOOt43rPC8hMg3xq+v775vpx3nb7J3nEfZmW+Sepflaa8az2bdVuRt5DkkiJFkjWjCik2YiekCuMG2wH+/ISPQA/APT7MId6U1EDxErTOPTDmYe2vp7wace/oHqXmUPZdoKFzotdyBZKtedEzqoBlLIPdo+SjZ/OUu/v5VhBnWMwyzksZhm2MwhKfIUP2oz/z2Mglhizujj761IsKrga1fX4LmDn90kJY7yHxfJwjiA7/wn3zFPRIJIMCGDH/wev55+A/OrhMssCQAA"
const rootStyles = "atm_mk_h2mmj6 atm_e2_1ylpe5n atm_ar_vrvcex atm_be_1q9ccgz atm_h_1h6ojuz atm_9s_1txwivl atm_vy_1osqo2v atm_gi_idpfg4";
const compactRootStyles = "atm_e2_1wugsn5";
const TagsRoot = /*#__PURE__*/styled('div')({
  name: "TagsRoot",
  class: "tdt2q95",
  propsAsIs: false
});
const leftRightButtonContainerStyles = "atm_tk_grho7r atm_mk_stnw88 atm_9s_1txwivl atm_ar_vrvcex atm_fc_1h6ojuz atm_h_1h6ojuz atm_e2_1osqo2v atm_wq_kb7nvz";
const leftStyles = "atm_fq_idpfg4 atm_1hh4tvs_ldb6wv atm_1054lsl_1osqo2v atm_10exmsa_glywfm atm_1938jqx_1yyfdc7 atm_135rde5_y22vw1";
const rightStyles = "atm_n3_idpfg4 atm_1v8yup7_ldb6wv atm_1oletfx_1osqo2v atm_va5d7e_glywfm atm_lzn0r3_1yyfdc7 atm_1aj71h3_458onv";
const compactLeftStyles = "atm_fq_idpfg4 atm_1hh4tvs_10yn01e atm_1054lsl_1osqo2v atm_10exmsa_glywfm atm_1938jqx_1yyfdc7 atm_135rde5_1i1n3iy";
const compactRightStyles = "atm_n3_idpfg4 atm_1v8yup7_10yn01e atm_1oletfx_1osqo2v atm_va5d7e_glywfm atm_lzn0r3_1yyfdc7 atm_1aj71h3_9fmkga";
const StyledButtonWrapper = /*#__PURE__*/styled('div')({
  name: "StyledButtonWrapper",
  class: "s1aranli",
  propsAsIs: false
});
interface IButton {
  compact?: boolean;
  onClick(): void;
}
const LeftButton = memo(({
  onClick,
  compact
}: IButton) => {
  return <div className={cx(leftRightButtonContainerStyles, compact ? compactLeftStyles : leftStyles)}>
            <StyledButtonWrapper>
                <ButtonLeft onClick={onClick} />
            </StyledButtonWrapper>
        </div>;
});
const RightButton = memo(({
  onClick,
  compact
}: IButton) => {
  return <div className={cx(leftRightButtonContainerStyles, compact ? compactRightStyles : rightStyles)}>
            <StyledButtonWrapper>
                <ButtonRight onClick={onClick} />
            </StyledButtonWrapper>
        </div>;
});
interface TagProps {
  onTagDelete?: (tag: string) => void;
  onTagClick?: (tag: string) => void;
  compact?: boolean;
  value?: string;
  tag: string;
}
const TagWrapper = memo((props: TagProps) => {
  const {
    onTagDelete,
    onTagClick,
    compact,
    value,
    tag
  } = props;
  const onClick = useCallback((_tag, event) => {
    if (typeof onTagClick !== 'function') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    onTagClick(value);
  }, [onTagClick, value]);
  const onDelete = useCallback(() => {
    if (onTagDelete) onTagDelete(value);
  }, [onTagDelete, value]);
  return <Tag data-testid="Chip__Tag" onDelete={onTagDelete ? onDelete : null} onClick={onClick} tag={tag} tagTitle={value} compact={compact} tagList />;
});
interface TagsPanelProps {
  onTagClick?: (tag: string) => void;
  onTagDelete?: (tag: string) => void;
  tags: Array<any>;
  compact?: boolean;
}
const TagsPanel = memo<TagsPanelProps>(props => {
  const {
    onTagClick,
    onTagDelete,
    compact,
    tags
  } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [showScrollRightArrow, setShowScrollRightArrow] = useState(false);
  const [showScrollLeftArrow, setShowScrollLeftArrow] = useState(false);
  const {
    ageGateActive
  } = useSecureState(SecureStateType.AGE_VERIFICATION);
  const safeTags = useMemo(() => {
    if (!ageGateActive || !tags) return tags;
    return tags.filter(tag => {
      const tagString = typeof tag === 'object' ? tag.key : tag;
      return !containsBadWord(tagString);
    });
  }, [ageGateActive, tags]);
  const checkArrowButtons = useThrottledCallback(() => {
    if (!ref.current) return;
    const {
      scrollWidth,
      offsetWidth,
      scrollLeft
    } = ref.current;
    setShowScrollRightArrow(offsetWidth + scrollLeft + 3 < scrollWidth);
    setShowScrollLeftArrow(scrollLeft > 3);
  }, 125);
  const slide = useCallback((shift: number) => {
    if (!ref.current) return;
    ref.current.scrollBy({
      left: shift,
      behavior: 'smooth'
    });
  }, []);
  const onClickLeft = useCallback((event?) => {
    event.preventDefault();
    event.stopPropagation();
    slide(-200);
  }, []);
  const onClickRight = useCallback((event?) => {
    event.preventDefault();
    event.stopPropagation();
    slide(200);
  }, []);
  const Tags = useMemo(() => {
    return safeTags.map((tag: any) => {
      const {
        value,
        key
      } = typeof tag === 'object' ? tag : {
        value: tag,
        key: tag
      };
      return <TagWrapper onTagClick={onTagClick} onTagDelete={onTagDelete} compact={compact} value={key} tag={value} key={key} />;
    });
  }, [compact, safeTags, onTagClick, onTagDelete]);
  useEffect(() => {
    checkArrowButtons();
  }, [Tags]);
  return <div className={cx(rootStyles, compact && compactRootStyles)} data-testid="Container__TagsPanel">
            {showScrollLeftArrow && <LeftButton onClick={onClickLeft} compact={compact} />}
            <TagsRoot onScroll={checkArrowButtons} ref={ref} className={cx('no-scrollbars', 'mfc-age-gate')}>
                {Tags}
            </TagsRoot>
            {showScrollRightArrow && <RightButton onClick={onClickRight} compact={compact} />}
        </div>;
});
export default TagsPanel;